/**
 * Z-Index Design Tokens
 *
 * Standardized stacking context to prevent z-index wars.
 * All z-index values in the app should reference these tokens.
 *
 * NOTE: AntD uses z-index 1000+ for modals/drawers/popups by default.
 * Our tokens are set above AntD's base to ensure custom overlays layer correctly.
 */

export const zIndex = {
  /** Default layer — page content */
  base: 0,
  /** Sticky elements — headers, toolbars */
  sticky: 100,
  /** Fixed navigation — sidebar, app bar */
  navigation: 200,
  /** Dropdowns and popovers */
  dropdown: 1050,
  /** Overlays and backdrops */
  overlay: 1060,
  /** Modals and dialogs (primary level) */
  modal: 1100,
  /** Nested modals (second level — opened from within a modal) */
  modalNested: 1200,
  /** AntD popups (Select/DatePicker/Cascader/Dropdown/TreeSelect dropdowns).
   * Must stay above modal + modalNested so a popup opened from a field inside
   * a (nested) modal never renders behind the modal mask. See index.css §11
   * "AntD popups MUST layer above modals" — that CSS rule is the actual
   * enforcement (AntD portals popups to document.body by default, outside
   * React context, so a CSS z-index is required as the safety net); keep the
   * two values in sync. */
  popup: 1250,
  /** Toasts and notifications */
  toast: 1300,
  /** Tooltips */
  tooltip: 1400,
  /** Maximum — dev tools, debug overlays, session expired */
  max: 9999,
  /** Skip navigation link — must sit above everything for a11y */
  skipNav: 99999,
} as const;

export type ZIndexKey = keyof typeof zIndex;
